import React from "react";
import { Search, SlidersHorizontal, Download, FilterX } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { BikeStatus, MaintenanceStatus } from "@/api/bikeService";

export type BikeFilter = {
  status?: BikeStatus;
  maintenanceStatus?: MaintenanceStatus;
  batteryLevel?: "low";
} | string;

interface BikesControlsProps {
  searchTerm: string;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  viewType: "table" | "list";
  onViewTypeChange: (value: "table" | "list") => void;
  activeFilter: BikeFilter | null;
  onFilterApply: (filter: BikeFilter) => void;
  onFilterClear: () => void;
  onExport: () => void;
}

const BikesControls: React.FC<BikesControlsProps> = ({
  searchTerm,
  onSearchChange,
  viewType,
  onViewTypeChange,
  activeFilter,
  onFilterApply,
  onFilterClear,
  onExport,
}) => {
  const getFilterName = (filter: BikeFilter): string => {
    console.log('Current filter:', filter);

    // Handle string filters
    if (typeof filter === 'string') {
      switch (filter) {
        case 'active':
          return 'Active Bikes';
        case 'inactive':
          return 'Inactive Bikes';
        case 'low_battery':
          return 'Low Battery';
        case 'needs_maintenance':
          return 'Needs Maintenance';
        default:
          return filter;
      }
    }
    
    // Handle object filters
    if (filter.status) {
      return filter.status === "active" ? "Active Bikes" : "Inactive Bikes";
    }
    
    if (filter.batteryLevel === "low") {
      return "Low Battery";
    }
    
    if (filter.maintenanceStatus) {
      return "Needs Maintenance";
    }
    
    console.warn('Unhandled filter type:', filter);
    return "Unknown Filter";
  };

  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
      <div className="relative flex-1">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search eBikes..."
          className="pl-8"
          value={searchTerm}
          onChange={onSearchChange}
        />
      </div>
      <div className="flex items-center gap-2">
        <Tabs
          defaultValue="table"
          value={viewType}
          onValueChange={(v) => onViewTypeChange(v as "table" | "list")}
          className="hidden sm:block"
        >
          <TabsList>
            <TabsTrigger value="table">
              <BarChart className="mr-2 h-4 w-4" />
              Table
            </TabsTrigger>
            <TabsTrigger value="list">
              <List className="mr-2 h-4 w-4" />
              List
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="flex items-center">
                <SlidersHorizontal className="mr-2 h-4 w-4" />
                Filters
                {activeFilter && <span className="ml-1 h-2 w-2 rounded-full bg-bike-primary"></span>}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onFilterApply("active")}>
                Active Bikes
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onFilterApply("inactive")}>
                Inactive Bikes
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onFilterApply("low_battery")}>
                Low Battery
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onFilterApply("needs_maintenance")}>
                Needs Maintenance
              </DropdownMenuItem>
              {activeFilter && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={onFilterClear} className="text-destructive focus:bg-destructive/10">
                    <FilterX className="mr-2 h-4 w-4" />
                    Clear Filters
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          {activeFilter && (
            <Button 
              variant="secondary" 
              size="sm" 
              className="bg-bike-primary text-white hover:bg-bike-primary/90 flex items-center gap-2"
              onClick={onFilterClear}
            >
              {getFilterName(activeFilter)}
              <FilterX className="h-4 w-4" />
            </Button>
          )}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">
              <Download className="mr-2 h-4 w-4" />
              Export
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onExport}>
              Export as CSV
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onExport}>
              Export as Excel
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onExport}>
              Export as PDF
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

// Import the missing icons
import { BarChart, List } from "lucide-react";

export default BikesControls;
